import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const milestones = [
  { year: '2018', title: 'The Beginning', desc: 'Founded with a vision to bridge the global talent gap between companies and skilled engineers.' },
  { year: '2020', title: 'First 50 Engineers', desc: 'Built our core network of vetted technology professionals across three countries.' },
  { year: '2022', title: 'Global Expansion', desc: 'Extended operations across 18 countries and 10+ industries, from fintech to healthtech.' },
  { year: '2024', title: '200+ Placements', desc: 'Surpassed 200 successful engineer placements, with clients ranging from startups to Fortune 500.' },
  { year: '2026', title: 'Full-Stack Partner', desc: 'Evolved into a dual-capability firm: end-to-end software development + flexible talent solutions.' },
];

export default function OurStory() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });

  return (
    <section className="py-16 md:py-24 bg-gray-50/50 relative overflow-hidden" ref={ref}>
      {/* Decorative background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-500/3 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-500/3 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-book-open-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Our Story</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            From a Simple Idea
            <br />
            <span className="text-primary-600">To a Global Force.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            What started as a belief that talent should be borderless has grown into a trusted partner for companies worldwide.
          </motion.p>
        </motion.div>

        <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -60, scale: 0.95 }}
            animate={inView ? { opacity: 1, x: 0, scale: 1 } : {}}
            transition={{ ...springTransition, duration: 0.8 }}
            className="w-full lg:w-5/12"
          >
            <div className="relative group">
              <div className="absolute -inset-2 bg-primary-500 rounded-[2rem] blur-2xl opacity-20 group-hover:opacity-35 group-hover:scale-[1.02] transition-all duration-700"></div>
              <div className="relative bg-white rounded-2xl overflow-hidden shadow-2xl border border-gray-100/50">
                <div className="relative w-full h-72 sm:h-80 md:h-[420px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=diverse%20group%20of%20tech%20founders%20and%20software%20engineers%20having%20a%20strategic%20meeting%20in%20a%20bright%20modern%20startup%20office%20with%20glass%20walls%20and%20green%20plants%20warm%20amber%20and%20white%20color%20palette%20professional%20corporate%20photography%20style%20clean%20minimalist%20environment&width=800&height=640&seq=our-story-thoughtic&orientation=landscape"
                    alt="Thoughtic Solutions founding story"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="absolute bottom-5 left-5 md:bottom-6 md:left-6 px-4 py-2.5 md:px-5 md:py-3 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg"
                  >
                    <div className="flex items-center space-x-2.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></div>
                      <span className="text-xs md:text-sm font-bold text-navy-900">Est. 2016</span>
                    </div>
                  </motion.div>
                </div>
              </div>
              <div className="absolute -bottom-3 -right-3 w-24 h-24 bg-primary-500/10 rounded-2xl -z-10"></div>
              <div className="absolute -top-3 -left-3 w-16 h-16 bg-emerald-500/10 rounded-2xl -z-10"></div>
            </div>
          </motion.div>

          {/* Content Side - Milestones */}
          <motion.div
            initial={{ opacity: 0, x: 60, scale: 0.95 }}
            animate={inView ? { opacity: 1, x: 0, scale: 1 } : {}}
            transition={{ ...springTransition, duration: 0.8, delay: 0.15 }}
            className="w-full lg:w-7/12"
          >
            <div className="space-y-8 md:space-y-10">
              <motion.p
                initial={{ opacity: 0, y: 25 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.45, duration: 0.5 }}
                className="text-base md:text-lg text-gray-600 leading-relaxed"
              >
                Thoughtic Solutions was born from a frustration we knew too well: brilliant companies struggling to find the right engineering talent, and exceptional developers overlooked because of geography. We set out to fix both sides of that equation.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 25 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.55, duration: 0.5 }}
                className="text-base md:text-lg text-gray-600 leading-relaxed"
              >
                Today, we operate at the intersection of software development and talent solutions — building products for clients who need full-stack teams, and placing pre-vetted engineers into companies that need to scale fast. Every engagement is built on transparency, speed, and a genuine commitment to outcomes.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, scaleX: 0 }}
                animate={inView ? { opacity: 1, scaleX: 1 } : {}}
                transition={{ delay: 0.65, duration: 0.6 }}
                className="h-px bg-gray-200 origin-left"
              />

              {/* Milestones */}
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-[19px] top-2 bottom-2 w-px bg-gray-200 hidden md:block"></div>
                <div className="space-y-6 md:space-y-8">
                  {milestones.map((m, i) => (
                    <motion.div
                      key={m.year}
                      initial={{ opacity: 0, y: 20 }}
                      animate={inView ? { opacity: 1, y: 0 } : {}}
                      transition={{ delay: 0.7 + i * 0.08, duration: 0.5 }}
                      className="flex flex-col md:flex-row md:items-start gap-2 md:gap-6"
                    >
                      <div className="flex items-center gap-3 md:gap-0">
                        <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-full shadow-md flex-shrink-0 z-10">
                          <span className="text-xs font-bold text-white">{m.year.slice(-2)}</span>
                        </div>
                        <span className="md:hidden text-lg font-bold text-navy-900">{m.year}</span>
                      </div>
                      <div className="md:pt-1.5">
                        <h4 className="hidden md:block text-sm font-bold text-primary-600 mb-0.5">{m.year}</h4>
                        <h3 className="text-base md:text-lg font-bold text-navy-900">{m.title}</h3>
                        <p className="text-sm md:text-base text-gray-500 mt-1 leading-relaxed">{m.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}